import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from '@/lib/next-shim'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ZoomOut from '@mui/icons-material/ZoomOut'
import Slider from '@mui/material/Slider'
import ZoomIn from '@mui/icons-material/ZoomIn'
import { Image } from '@/lib/next-shim'

export type PhotoViewerProps = {
  disableAnnotation?: boolean
  extension: string
  name: string
  onError?: (error?: unknown) => void
  onSave?: (file: File) => void
  src: string
  fallBackSrc?: string
}

export default function PhotoViewer(props: PhotoViewerProps) {
  const { disableAnnotation, extension, name, onError, onSave, src, fallBackSrc } = props
  const t = useTranslations()

  const imgRef = useRef<HTMLImageElement>(null)

  const [error, setError] = useState<boolean>(false)
  const [imgSrc, setImgSrc] = useState<string>()
  const [imageHeight, setImageHeight] = useState(0)
  const [zoom, setZoom] = useState<number>(50)
  const [zoomStyle, setZoomStyle] = useState<React.CSSProperties>({})

  const handleError = useCallback(() => {
    setImgSrc(fallBackSrc)
    setError(true)
    onError?.()
  }, [fallBackSrc, onError])

  useEffect(() => {
    if (src) {
      fetch(src)
        .then((response) => response.blob())
        .then((blob: Blob) => {
          setImgSrc(URL.createObjectURL(blob))
        })
        .catch(handleError)
    }
  }, [handleError, src])

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setImageHeight(event.currentTarget.offsetHeight)
    return
  }

  const handleZoomChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) return
    else {
      setZoom(newValue)
      if (imgRef && imgRef.current) {
        if (newValue < 50) {
          setZoomStyle({
            height: imageHeight - (50 - newValue) * 10,
            width: 'auto',
          })
        } else {
          setZoomStyle({
            height: imageHeight + (newValue - 50) * 10,
            width: 'auto',
          })
        }
      }
    }
  }

  const valueLabelFormat = (value: number) => {
    return `${value}%`
  }

  const zoomIn = () => {
    const currentZoom = zoom
    setZoom(currentZoom + 10)
    setZoomStyle({
      height: imageHeight + (currentZoom - 50) * 10,
      width: 'auto',
    })
  }

  const zoomOut = () => {
    const currentZoom = zoom
    setZoom(currentZoom - 10)
    setZoomStyle({
      height: imageHeight - (50 - currentZoom) * 10,
      width: 'auto',
    })
  }

  return (
    <Stack alignItems="center" spacing={2} sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ position: 'fixed', top: 0, left: 0, zIndex: (theme) => theme.zIndex.appBar + 1 }} />
      {imgSrc && <Image onLoad={handleLoad} style={zoomStyle} ref={imgRef} src={imgSrc} alt="viewer" onError={handleError} width={200} height={200} />}
      {error && (
        <Typography variant="subtitle1" component="div">
          {t('ui.filePreview.PhotoViewer.notAvailable')}
        </Typography>
      )}
      <Stack spacing={2} direction="row" sx={{ mb: 1, width: '50%', position: 'fixed', bottom: '30px' }} alignItems="center">
        <ZoomOut style={{ cursor: 'pointer' }} onClick={zoomOut} />
        <Slider valueLabelDisplay="on" valueLabelFormat={valueLabelFormat} max={150} min={10} step={10} value={zoom} onChange={handleZoomChange} />
        <ZoomIn style={{ cursor: 'pointer' }} onClick={zoomIn} />
      </Stack>
    </Stack>
  )
}
